import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FEATURE_FLAG } from '../../../constants/general';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import { VIEWS_MAP } from '../../../router/constants';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { useStore } from '../../../store';
import { SUPPLIER_REFERRER_TYPES } from '../../Supplier/consts';
import { useGetSupplierData } from '../../Supplier/hooks/useGetSupplierData';
import { ISupplier } from '../../Supplier/types';
import { StepSwitch } from '../components/StepSwitch';
import { STEPS, STYLES, WIZARD_MODE_STEPS } from '../consts';
import { getMailFees } from '../../MoveMoney/api/getMailFees';
import { getMailLimits } from '../../MoveMoney/api/getMailLimits';
import { getLowestLimit } from '../utils';
import { Layout } from '../../../components/Layout/Layout';

export const MailCheck = () => {
  const {
    setFeeData,
    setSelectedLimit,
    activeStep,
    setActiveStep,
    setPayeeDetails,
    resetMailCheckSlice,
    feeData,
    selectedLimit,
    setCheckAmount,
    setBillId,
    setContextType,
  } = useStore(state => state);
  const { state } = useLocation();
  const navigate = useNavigate();

  /** Queries */
  const { error, refetch: getLimits } = getMailLimits();
  const { refetch: getFeesData } = getMailFees();
  const { refetch: getSupplierList } = useGetSupplierData({ });
  /** Queries */

  const dynamicStyle = useMemo(() => {
    if (WIZARD_MODE_STEPS.includes(activeStep)) {
      return STYLES.APP_LAYOUT_WIZARD;
    }
    return STYLES.APP_LAYOUT;
  }, [activeStep]);

  const checkInitialStateData = async () => {
    if (error) {
      return;
    }
    if (state?.contextType) {
      setContextType(state?.contextType);
    }
    if (state?.billId) {
      setBillId(state.billId);
    }
    if (state?.transferAmount) {
      setCheckAmount(Number(state?.transferAmount));
    }
    if (state?.payeeDetails) {
      setPayeeDetails(state.payeeDetails);
    }
    if (state?.redirectStep) {
      setActiveStep(state.redirectStep);
    } else {
      const supplierListRes = await getSupplierList();
      const supplierList: ISupplier[] = supplierListRes.data.suppliers;
      if (!supplierList.length) {
        setActiveStep(STEPS.INTRO);
      } else {
        navigate(
          `/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`,
          { state: { referrer: SUPPLIER_REFERRER_TYPES.MAIL_CHECK, closeUrl: `/${VIEWS_MAP.MAIL_A_CHECK}` } },
        );
      }
    }
  };

  const handleLimits = () => {
    getLimits()
      .then(res => {
        const limitsData = res.data;
        if (limitsData) {
          const isLimitsValid = limitsData.monthlyLimit > 0;
          if (isLimitsValid) {
            getFeesData()
              .then(feeRes => {
                const feesData = feeRes.data;
                setFeeData(feesData?.fees?.[0]!);
                setSelectedLimit(getLowestLimit(limitsData));
                checkInitialStateData();
              });
          } else {
            setActiveStep(STEPS.NON_ELIGIBLE);
          }
        }
      });
  };

  useEffect(() => {
    if (!error) return;
    const { code, message } = error.response.data.errors[0];
    trackMixPanel('View Move Money Limit Error Screen', {
      'Error Code': code,
      'Error Message': message,
      Flow: 'Mail a Check',
    });
    setActiveStep(STEPS.NON_ELIGIBLE);
  }, [error]);

  useEffect(() => {
    setActiveStep(STEPS.LOADER);

    if (!validateFeatureFlag(FEATURE_FLAG.MAIL_CHECK)) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    }
    if (!state?.redirectStep) {
      resetMailCheckSlice();
    }
    if (feeData.fee && selectedLimit.key) {
      checkInitialStateData();
    } else {
      handleLimits();
    }
  }, []);

  useEffect(() => {
    if (activeStep === STEPS.LOADER) return;
    if (state?.payeeDetails?.supplierId && !state?.payeeDetails.supplierAddressData.line1) {
      navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.MANAGE.BASE}/${state.payeeDetails.supplierId}`, {
        state: {
          pageTitle: 'Mail a paper check',
          formHeader: 'Enter recipient\'s details',
          formType: 'MAIL_A_CHECK',
          onExitDestination: `/${VIEWS_MAP.DASHBOARD}`,
          isUpdateOnlyMode: true,
          isNativeBack: true,
        },
      });
    }
  }, [activeStep]);

  return (
    <Layout shouldHideChatBot wrapperClassName={dynamicStyle}>
      <ContentWrapper>
        <MainContent className={STYLES.MAIN_CONTENT}>
          <StepSwitch shouldShowSkeleton={!!state?.redirectStep} activeStep={activeStep} />
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};
